import get from 'lodash/get';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { Mousewheel, Navigation, Pagination } from 'swiper';
import {
  selectIsDesktop,
  selectIsMobile,
  selectIsTablet,
} from 'redux/selectors/layout';
import { getDocumentFields, getImage } from 'utils/helper';
import { CASE_STUDIES, PAGES } from 'utils/constants';
import Card from 'UI/components/Cards/Card';
import Overlay from 'UI/containers/Overlay';
import { routes } from 'utils/routes';
import styles from '../CardsSection.module.scss';

const SLIDES_PER_VIEW = {
  [routes.bookCall.slug]: {
    thirdSectionView: 'auto',
    fourthSectionView: 'auto',
  },
  [CASE_STUDIES.kitchenEquipment]: {
    firstSectionView: 'auto',
  },
  [CASE_STUDIES.aiPoweredComplianceTool]: {
    firstSectionView: 'auto',
  },
};

const SPECIFIC_SETTINGS = {
  [CASE_STUDIES.llmDocument]: {
    breakpoints: {
      0: {
        slidesPerView: 1,
        slidesPerGroup: 1,
        centeredSlides: true,
      },
      569: {
        slidesPerView: 1.3,
        slidesPerGroup: 1,
        centeredSlides: false,
      },
      769: {
        slidesPerView: 1.7,
        slidesPerGroup: 1,
        centeredSlides: false,
      },
      1025: {
        slidesPerView: 2.1,
        slidesPerGroup: 2,
        centeredSlides: false,
      },
    },
  },
  [CASE_STUDIES.fintechAnalysis]: {
    breakpoints: {
      0: {
        slidesPerView: 1,
        slidesPerGroup: 1,
        centeredSlides: true,
      },
      569: {
        slidesPerView: 1.8,
        slidesPerGroup: 1,
        centeredSlides: false,
      },
      769: {
        slidesPerView: 2.6,
        slidesPerGroup: 2,
        centeredSlides: false,
      },
      1025: {
        slidesPerView: 3.3,
        slidesPerGroup: 3,
        centeredSlides: false,
      },
    },
  },
  [CASE_STUDIES.aiConcierge]: {
    breakpoints: {
      0: {
        slidesPerView: 1,
        slidesPerGroup: 1,
        centeredSlides: true,
      },
      569: {
        slidesPerView: 1.8,
        slidesPerGroup: 1,
        centeredSlides: false,
      },
      769: {
        slidesPerView: 2.6,
        slidesPerGroup: 2,
        centeredSlides: false,
      },
      1025: {
        slidesPerView: 3.3,
        slidesPerGroup: 3,
        centeredSlides: false,
      },
    },
  },
  [CASE_STUDIES.aiVoiceAssistant]: {
    breakpoints: {
      0: {
        slidesPerView: 1,
        slidesPerGroup: 1,
        centeredSlides: true,
      },
      569: {
        slidesPerView: 1.8,
        slidesPerGroup: 1,
        centeredSlides: false,
      },
      769: {
        slidesPerView: 2.6,
        slidesPerGroup: 2,
        centeredSlides: false,
      },
      1025: {
        slidesPerView: 3.3,
        slidesPerGroup: 3,
        centeredSlides: false,
      },
    },
  },
  [CASE_STUDIES.aiEdtech]: {
    breakpoints: {
      0: {
        slidesPerView: 1,
        slidesPerGroup: 1,
        centeredSlides: true,
      },
      569: {
        slidesPerView: 1.9,
        slidesPerGroup: 1,
        centeredSlides: false,
      },
      769: {
        slidesPerView: 2.7,
        slidesPerGroup: 1,
        centeredSlides: false,
      },
      1025: {
        slidesPerView: 3.3,
        slidesPerGroup: 2,
        centeredSlides: false,
      },
    },
  },
  [CASE_STUDIES.aiAssistantPatients]: {
    breakpoints: {
      0: {
        slidesPerView: 1,
        slidesPerGroup: 1,
        centeredSlides: true,
      },
      569: {
        slidesPerView: 1.8,
        slidesPerGroup: 1,
        centeredSlides: false,
      },
      769: {
        slidesPerView: 2.6,
        slidesPerGroup: 2,
        centeredSlides: false,
      },
      1025: {
        slidesPerView: 3.3,
        slidesPerGroup: 3,
        centeredSlides: false,
      },
    },
  },
  [CASE_STUDIES.claudeSonnet]: {
    breakpoints: {
      0: {
        slidesPerView: 1,
        slidesPerGroup: 1,
        centeredSlides: true,
      },
      569: {
        slidesPerView: 1.8,
        slidesPerGroup: 1,
        centeredSlides: false,
      },
      769: {
        slidesPerView: 2.6,
        slidesPerGroup: 2,
        centeredSlides: false,
      },
      1025: {
        slidesPerView: 3.3,
        slidesPerGroup: 3,
        centeredSlides: false,
      },
    },
  },
};

const SPACE_BETWEEN = {
  [routes.bookCall.slug]: {
    thirdSectionView: 66,
    fourthSectionView: 43,
  },

  [CASE_STUDIES.digitalWallet]: {
    firstSectionView: 16,
  },
};

const REWIND = {
  [routes.bookCall.slug]: {
    firstSectionView: true,
    secondSectionView: true,
    thirdSectionView: true,
  },
};

const cardMapper = () => (card) => {
  if (card?.sys?.contentType?.sys?.id === 'article') {
    const {
      title,
      previewImageUrl,
      slug,
    } = getDocumentFields(card, [
      'title',
      'previewImageUrl',
      'slug',
    ]);

    const image = getImage(previewImageUrl);

    return {
      url: `${PAGES.blog}/${slug}`,
      image,
      title,
      children: <span>Read more</span>,
    };
  }

  const {
    contentList,
    images,
    contentModules: cardContent,
    text,
    title,
    withOverlay: specificCardOverlay,
    ...rest
  } = getDocumentFields(
    card,
    [
      'title',
      'contentList',
      'text',
      'images',
      'imagesBundles',
      'contentModules',
      'withOverlay',
    ],
  );

  const image = getImage(get(images, '[0]'));
  const icon = get(contentList, '[0]');
  const url = get(cardContent, '[0].fields.url');

  const modules = (cardContent || []).filter(
    (item) => item?.sys?.contentType?.sys?.id !== 'link',
  );
  const contentModules = specificCardOverlay ? modules : undefined;

  const items = specificCardOverlay ? undefined : modules
    .map((item) => getDocumentFields(item, ['title', 'contentList']));

  const children = (
    <Overlay className={styles.overlay}>
      <Card
        title={title}
        text={text}
      />
    </Overlay>
  );

  return {
    url,
    image,
    icon,
    text,
    title,
    children,
    items,
    contentModules,
    specificCardOverlay,
    ...rest,
  };
};

export default ({
  section,
  type,
  withSlider: sectionWithSlider,
  withOverlay,
  data,
  ...rest
}) => {
  const {
    title,
    description,
    subtitle,
    view,
    contentModules,
    images: rawImages,
  } = getDocumentFields(
    data || section,
    [
      'title',
      'description',
      'contentModules',
      'subtitle',
      'view',
      'images',
    ],
    {
      isNormilized: (data && !Object.keys(data).includes('fields'))
      || (section && !Object.keys(section).includes('fields')),
    },
  );

  const {
    contentModules: rawCardList,
    withoutBackground,
    disableSliderOnMobile,
    enableSliderOnDesktop,
    enableSliderOnMobile,
  } = getDocumentFields(
    get(contentModules, '[0]', []),
    [
      'contentModules',
      'withoutBackground',
      'disableSliderOnMobile',
      'enableSliderOnDesktop',
      'enableSliderOnMobile',
    ],
  );

  const images = (rawImages || []).map((rawImage) => getImage(rawImage));

  const cardList = (rawCardList || []).map(cardMapper(withOverlay));

  const ctaLink = get(contentModules, '[1]');

  const isMobileResolution = useSelector(selectIsMobile);
  const isTabletResolution = useSelector(selectIsTablet);
  const IsFullResolution = useSelector(selectIsDesktop);

  const withSlider = sectionWithSlider
  || enableSliderOnDesktop
  || (!disableSliderOnMobile && (isTabletResolution || isMobileResolution)) || (enableSliderOnMobile && isMobileResolution);

  const isShowNavigation = enableSliderOnDesktop || !(IsFullResolution && cardList?.length <= 3);

  const swiperProps = {
    modules: [Navigation, Mousewheel, Pagination],
    slidesPerView: 3,
    // slidesPerGroup: 3,
    spaceBetween: SPACE_BETWEEN[type]?.[view] || 32,
    centeredSlides: false,
    autoHeight: false,
    passiveListeners: true,
    rewind: REWIND[type]?.[view] || false,
    pagination: true,
    mousewheel: {
      forceToAxis: true,
    },
    breakpoints: {
      0: {
        slidesPerView: SLIDES_PER_VIEW[type]?.[view] || 1,
        slidesPerGroup: 1,
        centeredSlides: true,
      },
      768: {
        slidesPerView: SLIDES_PER_VIEW[type]?.[view] || 2,
        slidesPerGroup: 2,
        centeredSlides: false,
      },
      1024: {
        slidesPerView: SLIDES_PER_VIEW[type]?.[view] || 3,
        slidesPerGroup: 3,
        centeredSlides: false,
      },
    },
    ...SPECIFIC_SETTINGS[type],
  };

  const className = cn(
    styles.cardsSection,
    styles[type],
    styles[view],
    {
      [styles.withSlider]: withSlider,
    },
  );

  return {
    type,
    view,
    withSlider,
    cardList,
    ctaLink,
    title,
    description,
    subtitle,
    className,
    withoutBackground,
    swiperProps,
    withOverlay,
    isShowNavigation,
    images,
    ...rest,
  };
};
